import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { QuoteForm } from "../components/QuoteForm";
import { SEO } from "../components/SEO";
import { Shield, Eye, Wind, Sun, Check } from "lucide-react";
import { getServiceSchema } from "../utils/structuredData";

export const PergolaBlinds = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Pergola Blinds & Zip Screens | Weather Protection | Hudson Knox"
        description="Premium pergola blinds and zip screens for ultimate weather protection and privacy. Deponti Linea and Senso systems. Professional installation in South East England."
        keywords="pergola blinds, zip screens, outdoor blinds, weather protection, pergola screens, Linea blinds, Senso screens, windproof blinds"
        canonicalPath="/services/pergola-blinds"
        structuredData={getServiceSchema(
          "Pergola Blinds & Zip Screens",
          "Installation of Deponti Linea and Senso pergola blind systems",
          "https://hudsonknox.co.uk/services/pergola-blinds"
        )}
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1773270834655-d1bbd9393d7d)",
            filter: "brightness(0.4)"
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Pergola Blinds & Zip Screens</h1>
          <p className="text-xl text-neutral-200">Privacy, shade and weather protection</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-light text-neutral-900 mb-6">Complete Outdoor Comfort</h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Our outdoor zip screen blinds provide the perfect finishing touch to your pergola, offering protection 
              from wind, sun, and prying eyes. These sleek, retractable screens create an enclosed outdoor room 
              whenever you need it, while maintaining your views when you don't.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1771574208222-cfb813eb67c3"
                alt="Outdoor blinds"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-3xl font-light text-neutral-900 mb-6">Outdoor Zip Screens</h3>
              <p className="text-lg text-neutral-600 mb-6">
                Zip screens are advanced outdoor blinds that run in guided side channels, creating a weathertight seal. 
                They're ideal for pergolas, verandas, and patios where you want reliable wind and rain protection.
              </p>
              <ul className="space-y-3">
                {[
                  "Wind-resistant up to 60mph",
                  "UV protection and sun shading",
                  "Privacy without losing natural light",
                  "Available in manual or motorised",
                  "Range of fabrics and colours",
                  "Integrated with smart home systems"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-neutral-900" />
                    <span className="text-neutral-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light mb-12 text-center">Key Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Weather Protection",
                description: "Block wind and rain while maintaining airflow and comfort"
              },
              {
                icon: <Eye className="h-8 w-8" />,
                title: "Privacy Control",
                description: "Create private outdoor spaces without permanent structures"
              },
              {
                icon: <Wind className="h-8 w-8" />,
                title: "Wind Resistance",
                description: "Guided track system handles strong winds without flapping"
              },
              {
                icon: <Sun className="h-8 w-8" />,
                title: "Sun Shading",
                description: "Reduce glare and UV exposure by up to 95%"
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-lg mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-medium mb-3">{benefit.title}</h3>
                <p className="text-neutral-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fabric Options */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">Fabric Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Mesh Fabrics",
                description: "Maintain visibility and airflow while blocking UV rays and insects. Perfect for daytime use.",
                openness: "5% - 10%"
              },
              {
                title: "Blackout Fabrics",
                description: "Complete privacy and maximum sun protection. Ideal for creating enclosed outdoor rooms.",
                openness: "0%"
              },
              {
                title: "Clear PVC",
                description: "Weather protection while maintaining full visibility. Great for sheltering from wind and rain.",
                openness: "Clear"
              }
            ].map((fabric, index) => (
              <Card key={index} className="border-neutral-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-medium text-neutral-900 mb-3">{fabric.title}</h3>
                  <p className="text-neutral-600 mb-4">{fabric.description}</p>
                  <div className="inline-block bg-neutral-100 px-4 py-2 rounded">
                    <span className="text-sm font-medium text-neutral-900">Openness: {fabric.openness}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-light text-neutral-900 mb-6">Installation Options</h2>
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-medium text-neutral-900 mb-2">Manual Operation</h4>
                  <p className="text-neutral-600">
                    Simple crank handle operation for cost-effective blind control. Ideal for smaller installations.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium text-neutral-900 mb-2">Motorised Control</h4>
                  <p className="text-neutral-600">
                    Electric motor with remote control or wall switch. Can be integrated with smart home systems and weather sensors.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium text-neutral-900 mb-2">Smart Automation</h4>
                  <p className="text-neutral-600">
                    Full integration with home automation, voice control, and automatic weather response. 
                    Connect to Alexa, Google Home, or custom control systems.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1773270834655-d1bbd9393d7d"
                alt="Outdoor zip screens"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-neutral-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-white mb-4">Enhance Your Pergola</h2>
            <p className="text-lg text-neutral-400">Get a quote for outdoor blinds and zip screens</p>
          </div>
          <QuoteForm title="Request Pergola Blinds Quote" />
        </div>
      </section>
    </div>
  );
};
